import React from "react"
import Button from "@mui/material/Button"
import { navigate } from "gatsby"
import { Button as GatsbyButton } from "gatsby-theme-material-ui"
import { motion } from "framer-motion"
import { styled } from "@mui/material/styles"
import { useMenuContext } from "../components/utils/useMenuContext"
import { LiaAngleRightSolid } from "react-icons/lia"

const Navigation = styled(motion.ul)(({ isMobile }) => ({
  display: "flex",
  flexDirection: isMobile ? "column" : "row",
  gap: "2rem",
  margin: isMobile ? "2rem 0 0" : "0",
  padding: 0,

  width: "100%",
}))

const ParentItem = styled(motion.li)(({ theme, isActive, isMobile }) => ({
  color: "var(--white)",
  textTransform: "uppercase",
  listStyle: "none",
  position: "relative",
  "& .parentItemLink": {
    fontSize: "1.188rem !important",
    color: "var(--white)",
  },
  "& a": {
    textTransform: "capitalize",
    fontSize: "1.188rem",
    fontWeight: 900,

    "&:hover": {
      color: theme.palette.primary.main,
    },
  },
  "& svg": {
    transform: isActive ? "rotate(90deg)" : "rotate(0deg)",
    display: isMobile ? "inline-block" : "none",
  },
}))

const LinkWrapper = styled("span")(({ isMobile }) => ({
  display: "flex",
  flexDirection: "column",
}))
const LinkRow = styled("div")({
  display: "flex",
  gap: "0.5rem",
  alignItems: "center",
})

const ChildUl = styled(motion.ul)(({ isMobile }) => ({
  listStyle: "none",
  padding: "1rem",
  margin: 0,
  position: isMobile ? "relative" : "absolute",
  top: "100%",
  left: 0,
  backgroundColor: "var(--white)",
  borderRadius: "var(--ms1)",
  flexDirection: "column",
  opacity: isMobile ? 0 : 0,
  pointerEvents: "none",
  visibility: isMobile ? "visible" : "hidden",
  transition: "opacity 0.2s ease",
  display: isMobile ? "none" : "flex",
  boxShadow: isMobile ? "none" : "0 1px 2px 0 rgb(0 0 0 / 0.05)",

  [`${ParentItem}:hover &`]: !isMobile
    ? {
        opacity: 1,
        pointerEvents: "auto",
        visibility: "visible",
      }
    : {},
}))

const ChildItem = styled(motion.li)({
  listStyle: "none",
  minWidth: "140px",
  "& a": {
    color: "#002856",
    fontSize: "1rem !important",
    fontFamily: "Roboto Slab",
    fontWeight: 500,
    pointerEvents: "visible",
    opacity: 1,
    "&:hover": {
      color: "#eb7806",
    },
  },
})

const MainNavigation = ({ data, isMobile, handleCloseNavMenu: propClose }) => {
  const {
    navOpen,
    handleCloseNavMenu: contextClose,
    activeMenu,
    setActiveMenu,
  } = useMenuContext()

  const handleCloseNav = propClose || contextClose

  const handleClick = (i, e, hasChildren) => {
    if (isMobile) {
      e.preventDefault()
      if (hasChildren) {
        // Double-tap to navigate
        if (activeMenu === i) {
          navigate(e?.currentTarget?.href)
          handleCloseNav()
          setActiveMenu(null)
        } else {
          setActiveMenu(i)
        }
      } else {
        navigate(e?.currentTarget?.href)
        handleCloseNav()
        setActiveMenu(null)
      }
    } else {
      setActiveMenu(null)
      navigate(e?.currentTarget?.href)
      handleCloseNav()
    }
  }

  const LinkType = ({
    children,
    index,
    disableSubMenu,
    className,
    node,
    label,
    link,
  }) => {
    const isInternal = !!link?.internal
    const clickHandler = e =>
      handleClick(index, e, node?.childItems?.length > 0)

    const commonProps = {
      className,
      variant: "text",
      disableElevation: true,
      onClick: clickHandler,
      onMouseEnter: !disableSubMenu
        ? () => !isMobile && setActiveMenu(index)
        : undefined,
      size: "large",
    }

    return isInternal ? (
      <>
        <GatsbyButton {...commonProps} to={`/${link.internal.slug?.current}`}>
          {label}
        </GatsbyButton>
        {children}
      </>
    ) : (
      <>
        <Button {...commonProps} href={link?.external}>
          {label}
        </Button>
        {children}
      </>
    )
  }

  const parentVariants = {
    visible: { opacity: 1, pointerEvents: "auto" },
    hidden: { opacity: 0, pointerEvents: "none" },
  }

  return (
    <Navigation
      variants={parentVariants}
      animate={isMobile ? (navOpen ? "visible" : "hidden") : "visible"}
      isMobile={isMobile}
    >
      {data?.map((menuItem, index) => (
        <ParentItem
          key={menuItem._id || menuItem._key}
          isActive={activeMenu === index}
          isMobile={isMobile}
          onMouseLeave={() => !isMobile && setActiveMenu(null)}
        >
          <LinkWrapper isMobile={isMobile}>
            <LinkRow>
              <LinkType
                index={index}
                disableSubMenu={false}
                className="hoverUnderline parentItemLink"
                node={menuItem}
                link={menuItem?.link?.link}
                label={menuItem?.link?.text}
              />
              {menuItem.childItems?.length > 0 && (
                <LiaAngleRightSolid
                  style={{
                    fontSize: "1.2rem",
                    color: "var(--white)",
                  }}
                />
              )}
            </LinkRow>

            {menuItem.childItems?.length > 0 && (
              <ChildUl
                isMobile={isMobile}
                initial={false}
                animate={
                  isMobile
                    ? {
                        display: activeMenu === index ? "flex" : "none",
                        opacity: activeMenu === index ? 1 : 0,
                        pointerEvents: activeMenu === index ? "auto" : "none",
                        transition: { opacity: { duration: 0.2 } },
                      }
                    : {
                        opacity: activeMenu === index ? 1 : 0,
                        visibility: activeMenu === index ? "visible" : "hidden",
                        pointerEvents: activeMenu === index ? "auto" : "none",
                        transition: { opacity: { duration: 0.2 } },
                      }
                }
              >
                {menuItem.childItems.map(child => (
                  <ChildItem key={child._key}>
                    <LinkType
                      disableSubMenu
                      index={index}
                      className="hoverUnderline fontWeightNormal"
                      node={child}
                      link={child?.link}
                      label={child?.text}
                    />
                  </ChildItem>
                ))}
              </ChildUl>
            )}
          </LinkWrapper>
        </ParentItem>
      ))}
    </Navigation>
  )
}

export default MainNavigation
